import LatLng from 'interfaces/LatLng'
import {
  MIN_RADIUS,
  MAX_RADIUS,
  updateSearchCircle,
  updateSearchCircleRadius,
} from 'redux/features/map/slice'
import {
  makeSelectPrincipalBounds,
  makeSelectSearchCircle,
} from 'redux/features/map/selectors'
import { useAppDispatch, useAppSelector } from 'redux/hooks'
import useGoogleMap from './useGoogleMap'

interface ReturnProps {
  searchCircle:
    | {
        center: LatLng
        radius: number
      }
    | undefined
  computeSearchCircle: () => void
  resetSearchCircle: () => void
  setSearchCircleRadius: (newRadius: number) => void
}

const useSearchCircle = (): ReturnProps => {
  const map = useGoogleMap()
  const dispatch = useAppDispatch()
  const principalBounds = useAppSelector(makeSelectPrincipalBounds)
  const searchCircle = useAppSelector(makeSelectSearchCircle)

  const computeSearchCircle = () => {
    if (!map || !principalBounds) return
    if (!searchCircle) {
      // Get distance in meter
      const bounds = map.getBounds()?.toJSON()
      if (!bounds) return
      const ne = { lat: bounds.north, lng: bounds.east }
      const nw = { lat: bounds.north, lng: bounds.west }
      const se = { lat: bounds.south, lng: bounds.east }

      // Make sure the circle always fits the screen depending on map orientation (landscape/portrait)
      const distanceHorizontal =
        window.google.maps.geometry.spherical.computeDistanceBetween(
          new window.google.maps.LatLng(ne),
          new window.google.maps.LatLng(nw)
        )

      const distanceVertical =
        window.google.maps.geometry.spherical.computeDistanceBetween(
          new window.google.maps.LatLng(ne),
          new window.google.maps.LatLng(se)
        )
      const diameter = Math.min(distanceHorizontal, distanceVertical)
      const radius = diameter / 2
      // On ajoute un "padding" pour que les bords du cercle ne soient pas collés aux bords de l'écran
      let radiusWithPadding = Math.round(radius * 0.9)
      if (radiusWithPadding < MIN_RADIUS) radiusWithPadding = MIN_RADIUS
      if (radiusWithPadding > MAX_RADIUS) radiusWithPadding = MAX_RADIUS
      dispatch(
        updateSearchCircle({
          center: (map.getCenter() as google.maps.LatLng).toJSON(),
          radius: radiusWithPadding,
        })
      )
    }
  }

  const resetSearchCircle = () => dispatch(updateSearchCircle(undefined))

  const setSearchCircleRadius = (newRadius: number) =>
    dispatch(updateSearchCircleRadius(newRadius))

  return {
    searchCircle,
    computeSearchCircle,
    resetSearchCircle,
    setSearchCircleRadius,
  }
}

export default useSearchCircle
